import { useNavigate } from 'react-router-dom'
import { FolderKanban, CheckCircle2, Circle, Sparkles } from 'lucide-react'
import type { Project, ProjectStatus } from '@/types/entities'
import { SectionCard, EmptyState, Badge } from '@/components/ui'
import { date } from '@/lib/format'
import type { AccountDerived } from '../lib'

const STATUS_TONE: Record<ProjectStatus, 'ok' | 'info' | 'warn' | 'alert' | 'neutral'> = {
  'Not Started': 'neutral',
  'On Track': 'ok',
  'At Risk': 'warn',
  Behind: 'alert',
  Complete: 'ok',
}

export default function ProjectsTab({ d }: { d: AccountDerived }) {
  const navigate = useNavigate()
  const projects = d.projects

  if (!projects.length) {
    return (
      <SectionCard pad>
        <EmptyState icon={<FolderKanban size={36} />} title="No active projects" hint="Investor reporting, stabilization, CapEx, and onboarding projects for this account will appear here with progress and milestones." />
      </SectionCard>
    )
  }

  return (
    <div className="aw-stack">
      {projects.map((p: Project) => {
        const done = p.milestones?.filter((m) => m.done).length ?? 0
        const total = p.milestones?.length ?? 0
        return (
          <SectionCard
            key={p.id}
            eyebrow={p.type}
            title={
              <span className="aw-prj-title">
                {p.name}
                {p.wiseFlag && <span className="aw-wise-flag"><Sparkles size={12} /> WISE</span>}
              </span>
            }
            right={<Badge tone={STATUS_TONE[p.status]} variant="soft" dot>{p.status}</Badge>}
          >
            <div className="aw-prj-progress">
              <div className="aw-prj-bar-track">
                <div className={`aw-prj-bar aw-prj-bar--${STATUS_TONE[p.status]}`} style={{ width: `${p.pctComplete}%` }} />
              </div>
              <span className="aw-prj-pct num">{p.pctComplete}%</span>
            </div>
            <div className="aw-prj-meta">
              {p.owner && <span><span className="label">Owner</span> {p.owner}</span>}
              {p.dueDate && <span><span className="label">Due</span> {date(p.dueDate, 'med')}</span>}
              {total > 0 && <span><span className="label">Milestones</span> {done}/{total}</span>}
            </div>
            {p.milestones && p.milestones.length > 0 && (
              <ul className="aw-milestones">
                {p.milestones.map((m, i) => (
                  <li key={i} className={`aw-milestone${m.done ? ' aw-milestone--done' : ''}`}>
                    {m.done ? <CheckCircle2 size={14} className="aw-ms-done" /> : <Circle size={14} className="aw-ms-todo" />}
                    <span className="aw-ms-name">{m.name}</span>
                    {m.due && <span className="aw-ms-due num">{date(m.due, 'short')}</span>}
                  </li>
                ))}
              </ul>
            )}
            {p.propertyIds && p.propertyIds.length === 1 && (
              <button className="aw-prj-link" onClick={() => navigate(`/properties/${p.propertyIds![0]}`)}>View property →</button>
            )}
          </SectionCard>
        )
      })}
    </div>
  )
}
